import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ErrorHandler, Inject, Injectable, Optional } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { catchError, Observable, of } from 'rxjs';

/**
 * MatIconRegistry that never throws when an icon is missing.
 *
 * By default Angular Material routes a failed `getNamedSvgIcon(...)` to the global
 * `ErrorHandler` (Sentry), producing noisy "Unable to find icon ..." reports — typically
 * caused by a stale CDN/browser cache serving an outdated, non-fingerprinted icon sprite.
 * Here we intercept that failure, log a warning, and render an empty `<svg>` instead, so a
 * missing icon degrades gracefully (blank space) rather than surfacing as an exception.
 */
@Injectable()
export class SafeMatIconRegistry extends MatIconRegistry {
  constructor(
    @Optional() httpClient: HttpClient,
    sanitizer: DomSanitizer,
    @Optional() @Inject(DOCUMENT) private _doc: Document,
    errorHandler: ErrorHandler,
  ) {
    super(httpClient, sanitizer, _doc, errorHandler);
  }

  override getNamedSvgIcon(name: string, namespace: string = ''): Observable<SVGElement> {
    return super.getNamedSvgIcon(name, namespace).pipe(
      catchError((error) => {
        console.warn(
          `[icons] Icon "${namespace ? namespace + ':' : ''}${name}" not found. Rendering empty.`,
          error?.message ?? error,
        );
        return of(this._createEmptyIcon());
      }),
    );
  }

  private _createEmptyIcon(): SVGElement {
    const svg = this._doc.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('fit', '');
    svg.setAttribute('height', '100%');
    svg.setAttribute('width', '100%');
    svg.setAttribute('focusable', 'false');
    return svg;
  }
}
